import {
  findByDropdownItems,
  getLinkLabel,
  getTitleLabel,
  isLinkValid,
} from '@carboncopyco/editor-utils';
import { DownOutlineIcon, VOutlineIcon } from '@carboncopyco/icons';
import type {
  BlockInteractionsBlockBy,
  ILinkReplaceRule,
  LinkEditBy,
} from '@carboncopyco/types';
import {
  Dropdown,
  DropdownItem,
  InputText,
  TextCaption,
  TooltipBubble,
} from '@carboncopyco/ui';
import { useBoolean, useClickAway } from 'ahooks';
import type React from 'react';
import { type Dispatch, type SetStateAction, useEffect, useRef } from 'react';
import { InputField } from '../InputField';
import { getSelectorTypeToValue } from './utils';

export interface FindByDropDownProps {
  findBy: ILinkReplaceRule['findBy'];
  setFindBy: Dispatch<SetStateAction<LinkEditBy>>;
  elementMetadata: Partial<HTMLButtonElement & HTMLAnchorElement>;
  error?: React.ReactNode;
  elementType: ILinkReplaceRule['replaceType'];
  initialOldValue?: string;
}
export const FindByDropDown = ({
  findBy,
  setFindBy,
  elementMetadata,
  error,
  elementType,
  initialOldValue,
}: FindByDropDownProps) => {
  const dropdownContainerRef = useRef<HTMLDivElement>(null);
  const [isOpen, { set: setIsOpen, toggle: toggleOpen }] = useBoolean();
  useClickAway(() => setIsOpen(false), dropdownContainerRef);
  const selectorTypeToValue = getSelectorTypeToValue(elementMetadata);
  const isTitleValid = !!selectorTypeToValue.title?.length;
  const valueToPresent = initialOldValue ?? selectorTypeToValue[findBy];
  useEffect(() => {
    // Handle case when no href on the element.
    if (
      elementType === 'link' &&
      !initialOldValue &&
      !isLinkValid(selectorTypeToValue.url) &&
      isTitleValid
    ) {
      setFindBy('title');
    }
  }, [elementType, selectorTypeToValue.url]);

  const shouldDisableFindBy =
    elementType === 'button' ||
    !!initialOldValue ||
    (elementType === 'link' &&
      !isLinkValid(selectorTypeToValue.url) &&
      isTitleValid);
  return (
    <div
      className="flex flex-col gap-1.5"
      ref={dropdownContainerRef}
    >
      <InputField
        name="findBy"
        error={error}
        label={
          <Dropdown
            align="end"
            container={dropdownContainerRef.current}
            interactive
            width={272}
            open={isOpen}
            asChild
            onOpenChange={toggleOpen}
            disabled={shouldDisableFindBy}
            data-edit-tool="true"
            triggerElement={
              <div
                className="flex items-center"
                data-ds-dropdown-id="findBy"
              >
                <TextCaption
                  label={
                    findBy === 'title'
                      ? getTitleLabel(elementType)
                      : getLinkLabel()
                  }
                  color="text-ds-natural-600"
                />
                {!shouldDisableFindBy && <DownOutlineIcon size={12} />}
              </div>
            }
          >
            {findByDropdownItems.map((dropdownItem, index) => {
              return (
                <DropdownItem
                  asChild
                  key={index}
                  onClick={() => {
                    setFindBy(dropdownItem.key as BlockInteractionsBlockBy);
                  }}
                  iconRight={
                    findBy === dropdownItem.key ? <VOutlineIcon /> : null
                  }
                  label={dropdownItem.value}
                />
              );
            })}
          </Dropdown>
        }
      >
        <TooltipBubble
          title={valueToPresent}
          placement="bottom"
          appendTo="parent"
          width={250}
          interactive
        >
          <InputText
            disabled
            size="lg"
            value={valueToPresent}
            readOnly
            ellipsis
            state={error ? 'error' : 'normal'}
          />
        </TooltipBubble>
      </InputField>
    </div>
  );
};
